// src/components/assistant/ConfigurationTab.js
import React, { useEffect } from 'react';
import {
    Grid, GridItem, VStack, FormControl, FormLabel, Input, Slider, SliderTrack,
    SliderFilledTrack, SliderThumb, Select, Text, Checkbox, HStack, Button, Tooltip,
    Flex, Heading, Divider, useColorMode
} from '@chakra-ui/react';
import { MdAutorenew } from 'react-icons/md';
import { useAssistant } from "../../context/AssistantContext";

const ConfigurationTab = ({
    setIsDialogOpen,
    voiceLoading,
    voices,
    voiceRef,
    handleVoiceRefresh,
}) => {
    const {
        formState,
        setFormState,
        handleInputChange,
        handleSliderChange,
        handleCheckboxChange,
        handleUrlChange,
        formErrors,
        handleVoiceSettingsChange,
        handleVoiceChange,
    } = useAssistant();

    const { colorMode } = useColorMode();
    const isOpenAIRealtimeModel =
        formState.model?.startsWith('realtime-audio:');

    return (
        <VStack spacing={6} align="stretch">
            <Grid templateColumns={{
                base: "repeat(1, 1fr)",
                md: "repeat(1, 1fr)",
                lg: "repeat(2, 1fr)"
            }} gap={6}>
                {/* Left Column */}
                <GridItem>
                    <VStack spacing={4} align="stretch">
                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>Voice Setting</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                Configure the text-to-speech voice used by the AI during calls.
                            </Text>
                            <FormControl isRequired pb={5} isInvalid={formErrors?.voiceProvider}>
                                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Voice Provider</FormLabel>
                                <Select
                                    name="voiceProvider"
                                    value={formState.voiceProvider || ''}
                                    onChange={handleVoiceChange}
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    _hover={{
                                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                    }}
                                >
                                    <option value="OpenAI">OpenAI</option>
                                    <option value="Cartesia">Cartesia</option>
                                </Select>
                                {formErrors?.voiceProvider && (
                                    <Text color="red.500" fontSize="sm" mt={2}>
                                        {formErrors.voiceProvider}
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl isRequired pb={5}>
                                <Flex alignItems="center" justifyContent="space-between">
                                    <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'} mb="0">Voice</FormLabel>
                                    <Tooltip label="Refresh voice list">
                                        <Button
                                            size="sm"
                                            onClick={handleVoiceRefresh}
                                            isLoading={voiceLoading}
                                            variant="ghost"
                                            color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                                            _hover={{
                                                bg: colorMode === 'light' ? 'gray.100' : 'gray.600'
                                            }}
                                        >
                                            <MdAutorenew />
                                        </Button>
                                    </Tooltip>
                                </Flex>
                                <Select
                                    name="cartesiaVoiceId"
                                    value={formState.cartesiaVoiceId || ''}
                                    onChange={handleInputChange}
                                    ref={voiceRef}
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    _hover={{
                                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                    }}
                                >
                                    <option value="">Select a voice</option>
                                    {voices
                                        .filter(voice => voice.provider === formState.voiceProvider)
                                        .map((voice) => (
                                            <option key={voice.id} value={voice.id}>
                                                {voice.name}
                                            </option>
                                        ))}
                                </Select>
                            </FormControl>

                            <FormControl pb={5}>
                                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Voice Speed</FormLabel>
                                {formState.voiceProvider === 'Cartesia' ? (
                                    <>
                                        <Flex alignItems="center">
                                            <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Speed:</Text>
                                            <Text color="brand.400">{formState.voiceSpeed || 0}</Text>
                                        </Flex>
                                        <Slider
                                            aria-label="voice-speed-slider"
                                            value={formState.voiceSpeed || 0}
                                            onChange={(val) => handleSliderChange(val, 'voiceSpeed')}
                                            min={-1}
                                            max={1}
                                            step={0.1}
                                        >
                                            <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                                <SliderFilledTrack bg="blue.400" />
                                            </SliderTrack>
                                            <SliderThumb />
                                        </Slider>
                                    </>
                                ) : (
                                    <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'} fontSize="sm">
                                        Voice speed is only available for Cartesia voice provider
                                    </Text>
                                )}
                            </FormControl>
                        </Flex>

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>Interruption Handling</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                How many words the user must speak before the AI stops talking. Set to 0 to disable interruptions.
                            </Text>
                            <Flex alignItems="center">
                                <FormLabel flex="0 0 50%" color={colorMode === 'light' ? 'gray.700' : 'white'}>Number of Words Before Stop Talking</FormLabel>
                                <Input
                                    flex="0 0 30%"
                                    type="number"
                                    name="wordsInterruption"
                                    placeholder="0"
                                    min="0"
                                    max="10"
                                    step="1"
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    _hover={{
                                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                    }}
                                    _focus={{
                                        borderColor: 'blue.500',
                                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                    }}
                                    value={formState.wordsInterruption || 3}
                                    onChange={handleInputChange}
                                />
                            </Flex>
                        </Flex>

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Flex justifyContent="space-between" alignItems="center" pb={3}>
                                <Heading size="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>Webhooks</Heading>
                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                    Send call data to external URLs when events occur.
                                </Text>
                                <Button
                                    size="xs"
                                    colorScheme="blue"
                                    onClick={() => {
                                        const current = formState.webhooks || [];
                                        setFormState(prev => ({
                                            ...prev,
                                            webhooks: [...current, { url: '', user: '', password: '' }]
                                        }));
                                    }}
                                >
                                    + Add Webhook
                                </Button>
                            </Flex>

                            {(!formState.webhooks || formState.webhooks.length === 0) && (
                                <Text fontSize="sm" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} py={2}>
                                    No webhooks configured. Click "Add Webhook" to add one.
                                </Text>
                            )}

                            <VStack spacing={4} align="stretch">
                                {(formState.webhooks || []).map((webhook, index) => (
                                    <Flex
                                        key={index}
                                        direction="column"
                                        p={3}
                                        borderWidth={1}
                                        borderRadius="md"
                                        borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                        bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                    >
                                        <Flex justifyContent="space-between" alignItems="center" mb={2}>
                                            <Text fontSize="sm" fontWeight="medium" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                                                Webhook {index + 1}
                                            </Text>
                                            <Button
                                                size="xs"
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => {
                                                    const updated = [...(formState.webhooks || [])];
                                                    updated.splice(index, 1);
                                                    setFormState(prev => ({ ...prev, webhooks: updated }));
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </Flex>
                                        <Grid templateColumns="1fr 5fr" gap={3} alignItems="center">
                                            <GridItem>
                                                <FormLabel fontSize="sm" mb={0}>URL</FormLabel>
                                            </GridItem>
                                            <GridItem>
                                                <Input
                                                    size="sm"
                                                    placeholder="Enter webhook URL"
                                                    value={webhook.url || ''}
                                                    onChange={(e) => {
                                                        const updated = [...(formState.webhooks || [])];
                                                        updated[index] = { ...updated[index], url: e.target.value };
                                                        setFormState(prev => ({ ...prev, webhooks: updated }));
                                                    }}
                                                    type="url"
                                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                                    _hover={{ borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500' }}
                                                />
                                            </GridItem>
                                            <GridItem>
                                                <FormLabel fontSize="sm" mb={0}>Username</FormLabel>
                                            </GridItem>
                                            <GridItem>
                                                <Input
                                                    size="sm"
                                                    placeholder="Enter webhook username"
                                                    value={webhook.user || ''}
                                                    onChange={(e) => {
                                                        const updated = [...(formState.webhooks || [])];
                                                        updated[index] = { ...updated[index], user: e.target.value };
                                                        setFormState(prev => ({ ...prev, webhooks: updated }));
                                                    }}
                                                    type="text"
                                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                                    _hover={{ borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500' }}
                                                />
                                            </GridItem>
                                            <GridItem>
                                                <FormLabel fontSize="sm" mb={0}>Password</FormLabel>
                                            </GridItem>
                                            <GridItem>
                                                <Input
                                                    size="sm"
                                                    placeholder="Enter webhook password"
                                                    value={webhook.password || ''}
                                                    onChange={(e) => {
                                                        const updated = [...(formState.webhooks || [])];
                                                        updated[index] = { ...updated[index], password: e.target.value };
                                                        setFormState(prev => ({ ...prev, webhooks: updated }));
                                                    }}
                                                    type="password"
                                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                                    _hover={{ borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500' }}
                                                />
                                            </GridItem>
                                        </Grid>
                                    </Flex>
                                ))}
                            </VStack>
                        </Flex>

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column" gap={5}>
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>Auto Hangup Setting</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                Configure automatic call termination behavior.
                            </Text>

                            {/* hang up timeout */}
                            <Flex direction="column">
                                <Flex alignItems="center">
                                    <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Max Call Time in seconds:</Text>
                                    <Text color="brand.400">{formState.hangupTimeout}</Text>
                                </Flex>
                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={1}>
                                    The call will automatically end after this duration, regardless of the conversation state.
                                </Text>
                                <Slider
                                    aria-label="hangup-timeout-slider"
                                    value={formState.hangupTimeout}
                                    onChange={(val) => handleSliderChange(val, 'hangupTimeout')}
                                    min={30}
                                    max={600}
                                    step={10}
                                    focusThumbOnChange={false}
                                >
                                    <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                        <SliderFilledTrack bg="blue.400" />
                                    </SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </Flex>
                            <Checkbox
                                name="enableAiHangup"
                                isChecked={formState.enableAiHangup ?? true}
                                onChange={handleCheckboxChange}
                            >
                                Enable AI Hangup
                            </Checkbox>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mt={1}>
                                When enabled, the AI can decide to end the call based on the conversation context.
                            </Text>
                        </Flex>

                    </VStack>
                </GridItem>

                <GridItem>
                    <VStack spacing={4} align="stretch">
                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <FormControl isRequired>
                                <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>Deepgram STT Setting</Heading>
                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                    Speech-to-text model used to transcribe the caller's voice.
                                </Text>
                                <FormLabel pt={5} color={colorMode === 'light' ? 'gray.700' : 'white'}>Model ID</FormLabel>
                                <Select
                                    name="transcriptionModelId"
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    value={formState.transcriptionModelId || 'nova-3'}
                                    onChange={handleInputChange}
                                    required={true}
                                    colorScheme="orange"
                                >
                                    <option value='nova-3'>Nova-3</option>
                                    <option value='nova-2'>Nova-2</option>
                                    <option value='base'>Enhanced</option>
                                    <option value='enhanced'>Base</option>
                                </Select>
                            </FormControl>

                        </Flex>

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>Idle Setting</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                What the AI says and how long it waits when the caller goes silent.
                            </Text>
                            <FormControl isRequired pb={5}>
                                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Idle Message</FormLabel>
                                <Input
                                    placeholder="Enter idle message"
                                    name="idleMessage"
                                    value={formState.idleMessage || ''}
                                    onChange={handleInputChange}
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    _hover={{
                                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                    }}
                                    _focus={{
                                        borderColor: 'blue.500',
                                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                    }}
                                />
                            </FormControl>
                            <Flex alignItems="center">
                                <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Idle Timeout in seconds:</Text>
                                <Text color="brand.400">{formState.callTimeout || 20}</Text>
                            </Flex>
                            <Slider
                                aria-label="call-timeout-slider"
                                value={formState.callTimeout || 20}
                                onChange={(val) => handleSliderChange(val, 'callTimeout')}
                                min={5}
                                max={60}
                                step={1}
                                focusThumbOnChange={false}
                            >
                                <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                    <SliderFilledTrack bg="blue.400" />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Flex>

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>VAD Settings</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                These settings apply to OpenAI Realtime API only.
                            </Text>

                            <FormControl pb={4}>
                                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>VAD Type</FormLabel>
                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={2}>
                                    Server VAD uses audio level detection. Semantic VAD uses AI to understand when the user has finished speaking.
                                </Text>
                                <Select
                                    name="vadType"
                                    value={formState.vadType || 'server_vad'}
                                    onChange={handleInputChange}
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    _hover={{
                                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                    }}
                                >
                                    <option value="server_vad">Server VAD</option>
                                    <option value="semantic_vad">Semantic VAD</option>
                                </Select>
                            </FormControl>

                            {formState.vadType === 'semantic_vad' ? (
                                <FormControl pb={4}>
                                    <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Eagerness</FormLabel>
                                    <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={2}>
                                        How eager the AI is to respond. Lower values wait longer for the user to finish speaking.
                                    </Text>
                                    <Select
                                        name="vadEagerness"
                                        value={formState.vadEagerness || 'medium'}
                                        onChange={handleInputChange}
                                        bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                        color={colorMode === 'light' ? 'gray.800' : 'white'}
                                        borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                        _hover={{
                                            borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                        }}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </Select>
                                </FormControl>
                            ) : (
                                <>
                                    <FormControl pb={4}>
                                        <Flex alignItems="center">
                                            <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Threshold:</Text>
                                            <Text color="brand.400">{formState.vadThreshold ?? 0.5}</Text>
                                        </Flex>
                                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={1}>
                                            Sensitivity for detecting speech. Lower values detect softer speech, higher values require louder audio.
                                        </Text>
                                        <Slider
                                            aria-label="vad-threshold-slider"
                                            value={formState.vadThreshold ?? 0.5}
                                            onChange={(val) => handleSliderChange(val, 'vadThreshold')}
                                            min={0}
                                            max={1}
                                            step={0.05}
                                        >
                                            <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                                <SliderFilledTrack bg="blue.400" />
                                            </SliderTrack>
                                            <SliderThumb />
                                        </Slider>
                                    </FormControl>

                                    <FormControl pb={4}>
                                        <Flex alignItems="center">
                                            <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Silence Duration (ms):</Text>
                                            <Text color="brand.400">{formState.vadSilenceDurationMs ?? 500}</Text>
                                        </Flex>
                                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={1}>
                                            How long to wait after silence before the AI responds. Longer values give the user more time to pause mid-sentence.
                                        </Text>
                                        <Slider
                                            aria-label="vad-silence-slider"
                                            value={formState.vadSilenceDurationMs ?? 500}
                                            onChange={(val) => handleSliderChange(val, 'vadSilenceDurationMs')}
                                            min={100}
                                            max={2000}
                                            step={50}
                                        >
                                            <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                                <SliderFilledTrack bg="blue.400" />
                                            </SliderTrack>
                                            <SliderThumb />
                                        </Slider>
                                    </FormControl>

                                    <FormControl>
                                        <Flex alignItems="center">
                                            <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Prefix Padding (ms):</Text>
                                            <Text color="brand.400">{formState.vadPrefixPaddingMs ?? 300}</Text>
                                        </Flex>
                                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={1}>
                                            Amount of audio to include before detected speech begins, to avoid clipping the start of utterances.
                                        </Text>
                                        <Slider
                                            aria-label="vad-prefix-slider"
                                            value={formState.vadPrefixPaddingMs ?? 300}
                                            onChange={(val) => handleSliderChange(val, 'vadPrefixPaddingMs')}
                                            min={0}
                                            max={1000}
                                            step={50}
                                        >
                                            <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                                <SliderFilledTrack bg="blue.400" />
                                            </SliderTrack>
                                            <SliderThumb />
                                        </Slider>
                                    </FormControl>
                                </>
                            )}
                        </Flex>

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>Voicemail Handling</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                What happens when the call reaches a voicemail. "Skip" will hang up immediately. "Leave a Message" will let the AI leave a voicemail.
                            </Text>
                            <FormControl>
                                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Voicemail Action</FormLabel>
                                <Select
                                    name="voicemailAction"
                                    value={formState.voicemailAction || 'skip'}
                                    onChange={handleInputChange}
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    _hover={{
                                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                    }}
                                >
                                    <option value="skip">Skip / Hang Up</option>
                                    <option value="message">Leave a Message</option>
                                </Select>
                            </FormControl>
                        </Flex>

                        {isOpenAIRealtimeModel && (
                            <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                                <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>OpenAI Input Audio</Heading>
                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                    Configure how OpenAI Realtime handles incoming microphone audio before transcription.
                                </Text>
                                <FormControl>
                                    <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Noise Reduction</FormLabel>
                                    <Select
                                        name="inputAudioNoiseReduction"
                                        bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                        color={colorMode === 'light' ? 'gray.800' : 'white'}
                                        value={formState.inputAudioNoiseReduction || 'none'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="none">None</option>
                                        <option value="near_field">Near Field</option>
                                        <option value="far_field">Far Field</option>
                                    </Select>
                                </FormControl>
                            </Flex>
                        )}

                        <Flex bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} p={4} borderRadius="md" borderWidth={1} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} direction="column">
                            <Heading size="sm" pb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>OpenAI Voice Speed</Heading>
                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} pb={4}>
                                Controls the speed of the OpenAI Realtime voice output.
                            </Text>
                            <Flex alignItems="center">
                                <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Speed:</Text>
                                <Text color="brand.400">{formState.realtimeSpeed ?? 1.0}</Text>
                            </Flex>
                            <Slider
                                aria-label="realtime-speed-slider"
                                value={formState.realtimeSpeed ?? 1.0}
                                onChange={(val) => handleSliderChange(val, 'realtimeSpeed')}
                                min={0.25}
                                max={1.5}
                                step={0.05}
                            >
                                <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                    <SliderFilledTrack bg="blue.400" />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Flex>




                        <Checkbox
                            name="attachConversationHistory"
                            isChecked={formState.attachConversationHistory || false}
                            onChange={handleCheckboxChange}
                        >
                            Attached Last Conversation History
                        </Checkbox>

                        <Checkbox
                            name="twilioRecording"
                            isChecked={formState.twilioRecording || false}
                            onChange={handleCheckboxChange}
                        >
                            Enable Call Recording (There is additional charge for this. Refer to Twilio pricing)
                        </Checkbox>

                        <Checkbox
                            name="ignoreAudioDuringGreeting"
                            isChecked={formState.ignoreAudioDuringGreeting ?? false}
                            onChange={handleCheckboxChange}
                        >
                            Ignore Audio During Initial Greeting
                        </Checkbox>

                        <Checkbox
                            name="allowInterruptionDuringGreeting"
                            isChecked={formState.allowInterruptionDuringGreeting ?? true}
                            onChange={handleCheckboxChange}
                        >
                            Allow Interruption During Initial Greeting
                        </Checkbox>

                        {/* Delete button */}
                        <Button
                            colorScheme="red"
                            bgColor="red"
                            onClick={() => setIsDialogOpen(true)}
                            size="sm"
                            p={5}
                        >
                            Delete
                        </Button>
                    </VStack>
                </GridItem>
            </Grid>
        </VStack >
    );
};

export default ConfigurationTab;
